import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/daetfgbep053ijub1h60";

const principles = [
  "聚焦现象而非猎奇个人，真实场景做匿名与脱敏处理。",
  "坚持时间线梳理，用可核对的出处支撑结论。",
  "标注「持续跟进」的内容，绝不装作已有定论。",
  "理性讨论优先，拒绝无底线的扒私与网暴催化。",
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("contact_alt") || "").trim();

    // silently drop bot submissions but still show generic feedback
    if (honeypot) {
      setStatus("ok");
      form.reset();
      return;
    }
    data.delete("contact_alt");

    setSending(true);
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const failed =
        !res.ok ||
        !parsed ||
        parsed.code !== "OK" ||
        /spam/i.test(String(serverMsg)) ||
        /spam/i.test(responseText);

      if (failed) {
        setStatus("err");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试");
        return;
      }
      setStatus("ok");
      form.reset();
    } catch {
      setStatus("err");
      setFormError("网络似乎开小差了，请检查后重试。");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="about" className="relative w-full bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12">
          {/* about */}
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">About Us</p>
              <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">关于反差雷达</h2>
            </div>
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              反差雷达是一档专注「反差现象」的内容观察平台。我们整理反差爆料聊天记录、微信截图、私聊对话等公开讨论素材的时间线，
              把它拆解成可读的现象盘点与深度专题，帮你在纷繁的热点里，快速抓住来龙去脉。
            </p>
            <p className="text-sm leading-relaxed text-foreground-600">
              我们相信，多数「反差」并非非黑即白的人设崩塌，而是每个人在不同场景下的真实切面。因此，这里不追猎奇的料，
              只做讲得清、看得懂、也愿意停下来想一想的内容。
            </p>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">我们的内容原则</h3>
              <ul className="mt-4 flex flex-col gap-3">
                {principles.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                      <i className="ri-check-line text-xs font-bold" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-secondary-100 p-4">
                <i className="ri-time-line text-xl text-secondary-900" />
                <p className="mt-2 font-heading text-base font-bold text-foreground-950">全时更新</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">热点线索滚动追踪，重点事件持续补全。</p>
              </div>
              <div className="rounded-lg bg-secondary-100 p-4">
                <i className="ri-shield-check-line text-xl text-secondary-900" />
                <p className="mt-2 font-heading text-base font-bold text-foreground-950">克制把关</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">敏感素材匿名化，坚持理性围观。</p>
              </div>
              <div className="rounded-lg bg-secondary-100 p-4">
                <i className="ri-user-heart-line text-xl text-secondary-900" />
                <p className="mt-2 font-heading text-base font-bold text-foreground-950">开放共研</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">欢迎投稿线索，一起把现象聊透。</p>
              </div>
            </div>
          </div>

          {/* form */}
          <div className="lg:pl-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <h3 className="font-heading text-xl font-black text-foreground-950">投稿线索 / 与我们联系</h3>
              <p className="mt-2 text-sm text-foreground-600">
                发现值得关注的反差现象、线索或选题？欢迎留言。带 * 为必填项，我们会在核实后尽快回复你。
              </p>

              <form
                id="contact-form"
                data-readdy-form="contact-form"
                className="mt-6 flex flex-col gap-4"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="cf-name" className="text-sm font-medium text-foreground-800">
                      你的称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="cf-name"
                      name="name"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="cf-email" className="text-sm font-medium text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      required
                      maxLength={120}
                      placeholder="you@example.com"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="cf-topic" className="text-sm font-medium text-foreground-800">
                    事项类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="cf-topic"
                    name="topic"
                    required
                    defaultValue=""
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="" disabled>
                      请选择事项类型
                    </option>
                    <option value="投稿线索">投稿线索</option>
                    <option value="纠错反馈">内容纠错 / 补充</option>
                    <option value="栏目建议">栏目与专题建议</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="cf-msg" className="text-sm font-medium text-foreground-800">
                    内容详情 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="cf-msg"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请尽量描述现象线索、事件背景或你想交流的内容……（500 字以内）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="text-right text-xs text-foreground-500">最多 500 字</p>
                </div>

                {/* honeypot */}
                <div className="guard-trap" aria-hidden="true">
                  <label htmlFor="cf-contact">请勿填写此栏</label>
                  <input
                    id="cf-contact"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "ok" && (
                  <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                    已收到你的留言，感谢反馈，我们会尽快跟进。
                  </p>
                )}
                {status === "err" && (
                  <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-900">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin text-base" />
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2 text-base" />
                      提交留言
                    </>
                  )}
                </button>
                <p className="text-xs leading-relaxed text-foreground-500">
                  提交即表示你同意我们基于该信息进行核实与回复；我们不会把隐私信息用于其他用途。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}